import { useCallback, useState } from "react";
import {
  deleteDocument,
  markDocumentStored,
  requestDocumentUploadUrl,
  uploadDocumentToStorage
} from "../services/payments";
import { useUserState } from "../state/user";
import { PaymentDocumentType } from "../types/payments";
import { KYC_MAX_FILE_SIZE_BYTES, KYC_MIN_FILE_SIZE_BYTES } from "../constants";

/** Generates the document id the API expects the client to provide. */
function generateDocumentId(): string {
  // crypto.randomUUID is unavailable on insecure origins and older browsers.
  if (typeof crypto !== "undefined") {
    if (typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
      bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant 10xx
      const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join(
        ""
      );
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
        12,
        16
      )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  }
  console.error(
    "Cannot securely generate a new document ID: crypto.randomUUID and crypto.getRandomValues are unavailable, this browser must be very old"
  );
  throw new Error("Cannot securely generate a new document ID");
}

export type UploadValidationError =
  | "fileTooSmall"
  | "fileTooLarge"
  | "fileTypeNotAccepted";

/**
 * Checked before requesting an upload URL, so a bad file fails clearly.
 *
 * The type is checked here rather than left to the input's `accept`, which only
 * filters the picker: a dropped file bypasses it entirely, and the OS dialog
 * lets the filter be overridden anyway.
 */
export function validateFile(file: File): UploadValidationError | null {
  const accepted =
    file.type.startsWith("image/") || file.type === "application/pdf";
  if (!accepted) return "fileTypeNotAccepted";
  if (file.size < KYC_MIN_FILE_SIZE_BYTES) return "fileTooSmall";
  if (file.size > KYC_MAX_FILE_SIZE_BYTES) return "fileTooLarge";
  return null;
}

/**
 * Uploads a KYC document in the three steps the API requires:
 *
 *   1. PUT  /config/{configId}/documents      -> S3 endpoint + presigned policy
 *   2. POST to S3                             -> the file itself
 *   3. POST /config/{configId}/documents/{id} -> mark it as stored
 *
 * Step 3 only runs if step 2 actually succeeded. The mobile app swallows the
 * upload error and marks the document as stored anyway in a `finally`, which
 * records documents that were never uploaded and produces a corrupt KYC
 * submission. That behaviour is deliberately not reproduced here.
 */
export default function useDocumentUpload() {
  const { token } = useUserState();
  const [progress, setProgress] = useState<number | null>(null);

  const upload = useCallback(
    async ({
      configId,
      documentType,
      idGroup,
      file
    }: {
      configId: string;
      documentType: PaymentDocumentType;
      idGroup: string;
      file: File;
    }): Promise<string> => {
      if (!token) throw new Error("Missing access token");

      const documentId = generateDocumentId();
      // Which step threw decides whether there is a record to clean up: the
      // document only exists on the configuration once step 1 has returned.
      let step = "requestUploadUrl";
      try {
        setProgress(0);

        const target = await requestDocumentUploadUrl({
          authToken: token,
          configId,
          documentId,
          documentType,
          contentType: file.type,
          idGroup
        });

        step = "uploadToStorage";
        await uploadDocumentToStorage({
          target,
          file,
          onProgress: setProgress
        });

        // Only now is the document really there.
        step = "markStored";
        await markDocumentStored({ authToken: token, configId, documentId });

        return documentId;
      } catch (err) {
        // Not logged here: the error is rethrown below and the upload field
        // turns it into a message the professional can act on.
        //
        // Step 1 registers the document on the configuration before the file
        // exists, so a failure after it leaves a record that was never stored.
        // Dropping it keeps the slot honest: it goes back to empty instead of
        // reading as uploaded on the next reload. Best effort — if the cleanup
        // itself fails there is nothing the professional could do about it, so
        // it is not surfaced; they still get the upload error, which is the one
        // they can act on. This log is kept because it is the only trace such a
        // failure leaves anywhere — unlike the upload error, it is swallowed.
        if (step !== "requestUploadUrl") {
          // try {
          //   await deleteDocument({ authToken: token, configId, documentId });
          // } catch (cleanupError) {
          //   console.error(
          //     "[payments] could not drop the document record left behind by a failed upload",
          //     { configId, documentId },
          //     cleanupError
          //   );
          // }
        }
        throw err;
      } finally {
        setProgress(null);
      }
    },
    [token]
  );

  const remove = useCallback(
    async (configId: string, documentId: string) => {
      if (!token) throw new Error("Missing access token");
      return deleteDocument({ authToken: token, configId, documentId });
    },
    [token]
  );

  return { upload, remove, progress };
}
